import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null);
  const [ctaSettings, setCtaSettings] = useState({
    cta_title: 'Start selling on OpenGadget',
    cta_subtitle: 'Join our marketplace and turn your designs into reality with localized 3D manufacturing.'
  });

  // Auth context for visibility checks
  const { user, isAuthenticated, userProfile, loading: authLoading } = useAuth() || {};

  useEffect(() => {
    // Check if user has already voted on this article
    if (post?.id) {
      const savedVote = localStorage.getItem(`blog_vote_${post.id}`);
      if (savedVote) {
        setFeedbackSubmitted(savedVote);
      }
    }
  }, [post?.id]);

  useEffect(() => {
    const fetchPostData = async () => {
      try {
        setLoading(true);
        const data = await blogService.getPostBySlug(slug);
        setPost(data);
        
        // Fetch CTA settings based on the post's target page
        if (data) {
          // Record the 'Article View' session log activity!
          userService.recordActivity(
            user?.id || null, 
            navigator.userAgent, 
            'Article View', 
            data.id, // Log the Article ID as the activity details
            null
          ).catch(e => console.warn('Article view session tracking error:', e));

          // Dynamically increment the article views count!
          blogService.incrementPostViews(data.id).catch(e => console.warn('Views increment error:', e));

          const settings = await blogService.getPageSettings(data.target_page || data.targetPage || 'blog');
          if (settings) {
            setCtaSettings({
              cta_title: settings.cta_title,
              cta_subtitle: settings.cta_subtitle
            });
          }
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPostData();
  }, [slug]);

  const handleSentiment = async (isHelpful) => {
    if (feedbackSubmitted || !post) return;
    try {
      await blogService.incrementPostSentiment(post.id, isHelpful);
      const voteType = isHelpful ? 'helpful' : 'not-helpful';
      setFeedbackSubmitted(voteType);
      localStorage.setItem(`blog_vote_${post.id}`, voteType);
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-3xl mx-auto animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-6"></div>
            <div className="h-4 bg-slate-200 rounded w-1/4 mb-12"></div>
            <div className="h-64 bg-slate-100 rounded-2xl mb-12"></div>
            <div className="space-y-4">
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-full"></div>
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-red-50 rounded-full mb-6 text-red-500">
            <Icon name="AlertCircle" size={48} />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-4">{error}</h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">We couldn't find the article you were looking for. It may have been moved or deleted.</p>
          <Button onClick={() => navigate('/blog')}>Browse All Articles</Button>
        </div>
        <Footer />
      </div>
    );
  }

  // --- VISIBILITY CHECKS ---
  if (post.requires_auth && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 pt-32 pb-16 px-6 text-center flex flex-col items-center justify-center">
          <Icon name="Lock" size={64} className="text-slate-300 mb-6" />
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Sign In Required</h1>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">This article is restricted to registered members only.</p>
          <Button onClick={() => navigate('/signin', { state: { returnTo: location.pathname } })}>Sign In to Read</Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (post.visibility_roles && post.visibility_roles.length > 0 && isAuthenticated) {
    // If the post specifies roles, the user MUST have at least one of those roles
    // userProfile.user_roles is an array of objects: { id, name }
    const userRoleNames = (userProfile?.user_roles || []).map(r => r.name);
    const hasRequiredRole = post.visibility_roles.some(role => userRoleNames.includes(role));
    
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
          <Header />
          <div className="flex-1 pt-32 pb-16 px-6 text-center flex flex-col items-center justify-center">
            <Icon name="ShieldAlert" size={64} className="text-red-300 mb-6" />
            <h1 className="text-3xl font-bold text-slate-900 mb-4">Authorization Required</h1>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">Your account does not have the required permissions to view this specialized content.</p>
            <Button onClick={() => navigate('/blog')}>Return to Hub</Button>
          </div>
          <Footer />
        </div>
      );
    }
  }
  // -------------------------

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{post.title} | OpenGadget Resources</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://www.opengadget.com/${post.target_page || post.targetPage || 'blog'}/${post.slug}`} />
      </Helmet>

      <Header />
      
      {/* Article Header - DARK CONTRASTING BACKGROUND */}
      <div className="bg-slate-900 text-white pt-32 pb-16 border-b border-slate-800 shadow-inner">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 text-left">
                <Link 
                  to="/resources-hub" 
                  className="inline-flex items-center text-blue-400 hover:text-blue-300 font-bold group transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                  All Resources
                </Link>
                <div className="flex-1 max-w-xl">
                   <ResourceSearch placeholder="Search other resources..." />
                </div>
            </div>
            
            <div className="mb-6 text-left">
              <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                {post.category?.name}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight tracking-tight text-left">
              {post.title}
            </h1>
            
            <div className="flex items-center text-slate-400 text-sm font-medium">
              <div className="flex items-center mr-6">
                <Icon name="Calendar" size={16} className="mr-2 text-blue-500" />
                {post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'Recently'}
              </div>
              <div className="flex items-center">
                <Icon name="Clock" size={16} className="mr-2 text-blue-500" />
                {Math.ceil((post.content || '').replace(/<[^>]*>?/gm, '').split(' ').length / 200)} min read
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          {post.featured_image && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
              <img src={post.featured_image} alt={post.title} className="w-full h-auto" />
            </div>
          )}
          
          <div 
            className="prose prose-lg prose-slate max-w-none text-left
              prose-headings:text-slate-900 prose-headings:font-bold
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-li:text-slate-600
              prose-strong:text-slate-900
              prose-a:text-blue-600 hover:prose-a:text-blue-700"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          
          {/* Share / Footer */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="text-slate-800 font-bold">
                {feedbackSubmitted ? 'Thank you for your feedback!' : 'Was this article helpful?'}
              </div>
              <div className="flex gap-4">
                <button 
                  disabled={!!feedbackSubmitted}
                  onClick={() => handleSentiment(true)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all border ${
                    feedbackSubmitted === 'helpful' 
                      ? 'bg-green-50 border-green-200 text-green-700 shadow-sm' 
                      : feedbackSubmitted 
                        ? 'bg-slate-50 border-slate-100 text-slate-300 opacity-50'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  <Icon name="ThumbsUp" size={16} className="mr-2" />
                  <span>Yes</span>
                  {feedbackSubmitted && (
                    <span className="ml-2 pl-2 border-l border-current font-bold">
                      {post.helpful_count + (feedbackSubmitted === 'helpful' ? 1 : 0)}
                    </span>
                  )}
                </button>

                <button 
                  disabled={!!feedbackSubmitted}
                  onClick={() => handleSentiment(false)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all border ${
                    feedbackSubmitted === 'not-helpful' 
                      ? 'bg-red-50 border-red-200 text-red-700 shadow-sm' 
                      : feedbackSubmitted 
                        ? 'bg-slate-50 border-slate-100 text-slate-300 opacity-50'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  <Icon name="ThumbsDown" size={16} className="mr-2" />
                  <span>No</span>
                  {feedbackSubmitted && (
                    <span className="ml-2 pl-2 border-l border-current font-bold">
                      {post.not_helpful_count + (feedbackSubmitted === 'not-helpful' ? 1 : 0)}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {/* Related CTA - DYNAMIC FROM DATABASE */}
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-extrabold mb-4">{ctaSettings.cta_title}</h3>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto font-medium">
                {ctaSettings.cta_subtitle}
              </p>
              <Link to="/seller-signup">
                <Button 
                  variant="default" 
                  className="bg-white text-blue-600 hover:bg-blue-50 border-none px-10 py-4 text-lg font-bold rounded-xl shadow-lg shadow-black/10"
                >
                  Get Started for Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPost;
